/*
	This package supplements Filtering.js.  Filtering provides a low level interface that translates directly to
	Flashtalking API request parameters, while this one maintains state that is suitable for use in the frontend
	interface, which may then be composed into an `advancedFilter` for the API request.

	Call `addColumnFilters()` with a reducerConfig object (see readme in reducers directory), to add state for filtering:
		String: Search for a substring in the column attribute
		Range: Search for a value within a range
		Date: Search for a date within a range
		Checkbox: Search for specific values with a checkbox selection.

	columnFilters state is stored as a hash with two objects: `active` and `defaults`.
		`active` represents the current selection
		`defaults` is used to store column filter configuration

	When calling `addColumnFilters()`, you can specify an optional second object with columnDefaults, to configure filters.
	e.g.

	```
		reducerConfig = addColumnFilters(reducerConfig, {
			mediaBuyerName: {
				name: "mediaBuyerName",
				type: "String",
				displayName: "Media Buyer",
			},
			mediaBuyerEmail: {
				name: "mediaBuyerEmail",
				type: "Checkbox",
				displayName: "Media Buyer E-mail",
			},
			dateCreated: {
				name: "dateCreated",
				type: "Date",
				displayName: "Date Created",
			},
		});
	```

	ColumnFilters are expected to be formatted as:
		{
			type: "String" | "Range" | "Date" | "Checkbox",
			name: <columnName>,
			displayName: <string>, // User friendly label, e.g. "Start Date", instead of "startDate"
			params: [<param1>, <param2>, ...],
			options: [<option1>, <option2>, ...], // Options for checkbox selection, or valid range bounds, as [lowBound, highBound]
		}

	`params` represents the user's filter selection.
		- For a `String`, it's just the search string.
		- For a `Range` or `Date`, it's a 2 element array, [lowBound, highBound], inclusive
		- For a `Checkbox`, it's an array of all of the selected elements, as strings.


	### Optional ColumnFilter properties:

	You may also optionally add properties:
		`dictionaryName`
			- Name of an entry in DICTIONARIES (from common/dictionaries), which does a translation of a
			UI friendly param into a value that the API expects.
				- e.g. If the api has a value like "rejected", but you want the UI to show a checkbox filter where
					the option presented to the user says "Not Cool", you can add a dictionary for that.
					- See: common/dictionaries for details.
			- When present, apiToUI translations will happen automatically when updating a filter's `options`, either
			via `setColumnFilter`, or `updateColumnFilter`, and uiToAPI translations will happen when calling
			`generateAdvancedFilter`.
				- If you want to skip translation, you can pass an optional boolean argument to `setColumnFilter` and
				`updateColumnFilter`
					- e.g. dispatch(setColumnFilter("approval", newFilter, false));

		`filterParser`
			- Function that overrides the default behavior translating the filter's params into the `advancedFilter`
				clause used by the Flashtalking API.
			- This is useful if you're creating a composite field, and need to decompose it at API request time.
				The function should take a name and params, and return a string that will be used in its
				`advancedFilter` clause.

	When using both `dictionaryName` and `filterParser`, dictionary translation will occur on params before
	running the `filterParser` function.  This shouldn't generally change how you'd write your filterParser function,
	but it's something to be aware of if you have a very touchy case.


	### Action Creators

	Calling `addColumnFilters()` will add the following action creators:
		setColumnFilter(filter) // Completely overwrites the filter matching `filter.name` or adds a new filter.
		updateColumnFilter(name, filterPartial) // Overwrites only the properties provided in `filterPartial` on the named filter.
		removeColumnFilter(name) // Completely removes the named filter.
		clearColumnFilters() // Just clears the `params` selection for existing filters.

	In order to preserve filter configuration, it's recommended that you stick to `updateColumnFilter()`
	and `clearColumnFilters()`.


	### Dates

	The API expects dates to be in a specific format.  Use the API_DATE_FORMAT constant to format them when
	setting a Date filter selection to ensure the API understands your filter.

	Notably, the Rhombus `Dates` date range picker component sends Moment.js objects to its change handlers,
	and these have to be formatted.

	e.g.
	```
		import { SomeReducer } from "reducers/SomeReducer";
		import { API_DATE_FORMAT } from "utils/constants";
		...
		onChange(momentObjectStartDate, momentObjectEndDate){
			let formattedStartDate = momentObjectStartDate.format(API_DATE_FORMAT);
			let formattedEndDate = momentObjectEndDate.format(API_DATE_FORMAT);
			let params = [formattedStartDate, formattedEndDate];
			dispatch(Reducer.updateColumnFilter("startDate", { params }));
		}
	```
*/

import { escapeParensForAPI } from "utils/common";
import DICTIONARIES from "./dictionaries";

export const COLUMN_FILTERS_NAMESPACE = "columnFilters";

/*
	ColumnFilters Default State
*/
export const defaultColumnFiltersState = {
	active: {},
	defaults: {},
};

/*
	Action Creator Generators
*/
export function generateSetColumnFilter(type){
	return function setColumnFilter(filter, shouldTranslate = true){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { filter, shouldTranslate },
			});
		};
	};
}

export function generateUpdateColumnFilter(type){
	return function updateColumnFilter(name, filterPartial, shouldTranslate = true){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { name, filterPartial, shouldTranslate },
			});
		};
	};
}

export function generateRemoveColumnFilter(type){
	return function removeColumnFilter(name){
		return (dispatch, getState) => {
			dispatch({
				type,
				payload: { name },
			});
		};
	};
}

export function generateClearColumnFilters(type){
	return function clearColumnFilters(){
		return (dispatch, getState) => {
			dispatch({
				type,
			});
		};
	};
}


/*
	Reducers (action handlers)
*/
export function replaceColumnFilter(state, action){
	// Completely overwrites an existing filter.
	let { filter, shouldTranslate } = action.payload;

	let dictionary = DICTIONARIES[filter.dictionaryName];
	let translatedFilter = shouldTranslate ? translateOptions(filter, dictionary) : filter;

	return {
		...state,
		[COLUMN_FILTERS_NAMESPACE]: {
			...state[COLUMN_FILTERS_NAMESPACE],
			active: {
				...state[COLUMN_FILTERS_NAMESPACE].active,
				[filter.name]: translatedFilter,
			},
		},
	};
}

export function patchColumnFilter(state, action){
	/*
		Overwrites only the properties defined in `action.payload.filterPartial`

		Particularly useful for leaving a filter's configuration in place and just changing
		`params` based on user selection.
	*/
	let { name, filterPartial, shouldTranslate } = action.payload;
	let filter = state[COLUMN_FILTERS_NAMESPACE].active[name];
	let dictionary = DICTIONARIES[filterPartial.dictionaryName || filter.dictionaryName];
	let translatedFilterPartial = shouldTranslate ? translateOptions(filterPartial, dictionary) : filterPartial;

	if(!filter || !filter.name){
		console.warn("Unable to map Filter");
		console.warn(filter, name, filterPartial);
		return state;
	}

	return {
		...state,
		[COLUMN_FILTERS_NAMESPACE]: {
			...state[COLUMN_FILTERS_NAMESPACE],
			active: {
				...state[COLUMN_FILTERS_NAMESPACE].active,
				[filter.name]: {
					...filter,
					...translatedFilterPartial,
				},
			},
		},
	};
}

export function deleteColumnFilter(state, action){
	/*
		This generally won't be used.
		If you want to clear a filter, just use `updateColumnFilter()` with an empty `params`.

		e.g.
			dispatch(updateColumnFilter("someColumnName", { params: {} }));
	*/

	let { name } = action.payload;

	let filters = state[COLUMN_FILTERS_NAMESPACE];
	let newFilters = {};
	for(let key in filters){
		if(key !== name){
			newFilters[key] = filters[key];
		}
	}

	return {
		...state,
		[COLUMN_FILTERS_NAMESPACE]: newFilters,
	};
}

export function clearColumnFilters(state, action){
	/*
		We would normally just restore from defaults, but we don't want to unload stuff like checkbox options
		that have been fetched from the server.

		Instead, we just unset `params` on each active filter.

		TODO: Given that we just unset params to clear filters, and we're not supporting changing filter types
		at runtime, it may not make sense to store the defaults here at all.
	*/
	let { active, defaults } = state[COLUMN_FILTERS_NAMESPACE];
	let keys = Object.keys(active);

	let newActive = keys.reduce((prev, key) => {
		let filter = active[key];
		prev[key] = {
			...filter,
			params: undefined, /*
				We set `params` to `undefined` in case something wants to use destructuring assignment
				with default fallback values.
				e.g.
					let filter = useSelector(state => state.path.to.filter);
					let { params = [] } = filter;
					let [startDate, endDate] = params;

				`null`, etc. is a defined value and would take precedence over the default (empty array), and would
				cause an error when you attempt that array destructuring assignment.
			*/
		};

		return prev;
	}, {});

	return {
		...state,
		[COLUMN_FILTERS_NAMESPACE]: {
			active: newActive,
			defaults,
		},
	};
}

export function addColumnFilters(
	{
		typeRoot,
		actionTypes,
		initialState,
		handlers,
		actionCreators,
	},
	columnDefaults = {}
){
	let newInitialState = {
		...initialState,
		[COLUMN_FILTERS_NAMESPACE]: {
			...defaultColumnFiltersState,
			active: {
				...columnDefaults,
			},
			defaults: columnDefaults,
		},
	};

	let setColumnFilterType = `${typeRoot}_SET_COLUMN_FILTER`;
	let updateColumnFilterType = `${typeRoot}_UPDATE_COLUMN_FILTER`;
	let removeColumnFilterType = `${typeRoot}_REMOVE_COLUMN_FILTER`;
	let clearColumnFiltersType = `${typeRoot}_CLEAR_COLUMN_FILTERS`;

	let newTypes = {
		...actionTypes,
		[setColumnFilterType]: setColumnFilterType,
		[updateColumnFilterType]: updateColumnFilterType,
		[removeColumnFilterType]: removeColumnFilterType,
		[clearColumnFiltersType]: clearColumnFiltersType,
	};

	let newActionCreators = {
		...actionCreators,
		setColumnFilter: generateSetColumnFilter(setColumnFilterType),
		updateColumnFilter: generateUpdateColumnFilter(updateColumnFilterType),
		removeColumnFilter: generateRemoveColumnFilter(removeColumnFilterType),
		clearColumnFilters: generateClearColumnFilters(clearColumnFiltersType),
	};

	let newHandlers = {
		...handlers,
		[setColumnFilterType]: replaceColumnFilter,
		[updateColumnFilterType]: patchColumnFilter,
		[removeColumnFilterType]: deleteColumnFilter,
		[clearColumnFiltersType]: clearColumnFilters,
	};

	return {
		typeRoot,
		initialState: newInitialState,
		types: newTypes,
		actionCreators: newActionCreators,
		handlers: newHandlers,
	};
}

/*
	After our filters are configured, we need to compile them into a parameter for an API request.
	We do this via the `advancedFilter` param.

	Full documentation for Advanced Filters here: http://qa01-jcrm.crm.irl11.ftdns.net/swagger-ui.html

	The general strategy is to map each filter to a clause, depending on the filter type,
	and then combine them with an `all(<clause1>,<clause2>,...)`.
*/
const filterMaps = {
	// These will generate a server 500 response if you put a space after the comma after the `columnName`.
	// Escaping is applied to parens in String and Checkbox filter params.
	String: (columnName, params) => {
		// Search for a pattern with wildcards on either end.
		let escapedParams = escapeParensForAPI(params);
		return `like(${columnName},*${escapedParams}*)`;
	},
	Checkbox: (columnName, params) => {
		// Search for values in the set of selected options.
		let escapedParams = params.map(escapeParensForAPI);
		let commaSeparatedOptions = escapedParams.join(",");
		return `in(${columnName},${commaSeparatedOptions})`;
	},
	Exclude: (columnName, params) => {
		// Search for values excluding the set of selected options.
		let escapedParams = params.map(escapeParensForAPI);
		let commaSeparatedOptions = escapedParams.join(",");
		return `not_in(${columnName},${commaSeparatedOptions})`;
	},
	Range: (columnName, params) => {
		// Search for values between the selected range bounds (inclusive)
		let [start, end] = params;
		return `between(${columnName},${start},${end})`;
	},
	Date: (columnName, params) => {
		// Search for dates between the selected date range bounds (inclusive)
		let [start, end] = params;
		return `between(${columnName},${start},${end})`;
	},
};

export function generateAdvancedFilter(columnFilters){
	/*
		Note that dictionary translation happens before applying the filterParser.

		This is probably only a niche case that you need both, but if you have both,
		that order is significant, and a potential cause of bugs if you're not expecting it.
	*/
	let columnKeys = Object.keys(columnFilters);

	if(columnKeys.length === 0){
		return "";
	}

	let filterArgs = columnKeys.map((key) => {
		let filter = columnFilters[key];

		if(isActiveFilter(filter)){
			let { type, name, params, filterParser, dictionaryName } = filter;
			let dictionary = DICTIONARIES[dictionaryName]?.uiToAPI;

			if(dictionary){
				// Translate user-friendly values back to API values
				params = params.map(value => dictionary[value] || value);
			}
			if(filterParser){
				return filter.filterParser(name, params);
			}

			return filterMaps[type](name, params);
		}

		// If the filter isn't active it doesn't need to be queried for
		return null;
	});

	let nonEmptyFilters = filterArgs.filter(item => item);
	if(nonEmptyFilters.length === 0){
		return "";
	}

	let commaSeparatedFilters = nonEmptyFilters.join(",");

	return `all(${commaSeparatedFilters})`;
}


/*
	Active Filter Utilities

	Some UI elements, like the FitlerTagList, should be hidden when there are no
	active filters, and in case we change how this state is stored, it's
	helpful to have a single definitive way to check for this.
*/

export function isActiveFilter(filter){
	/*
		Returns whether or not a column filter is active.  `true` if it is.  `false` otherwise.

		A filter is considered active if it has specified `params`.

		Generally, this means its `params` property is a string (for a String filter),
		or a populated array (for Range, Date, or Checkbox filters).

		Either way, you can just check for a truthy `length` property.
	*/

	return (filter?.params?.length);
}

export function hasActiveFilters(state){
	/*
		Checks a branch of state for active column filters.
		Returns `true` if there are active column filters.  `false`, otherwise.

		This should be called on the same level of state where `addColumnFilters()` is applied.
		i.e. So that `state[COLUMN_FILTERS_NAMESPACE]` contains the column filter data.

		e.g. If the Libraries reducer has column filters added so that the store looks like:
			```
				state: {
					libraries: {
						columnFilters: {
							active: { ... }
						}
					}
				}
			```

			You could check for active filters in a component like:
			```
				import { useSelector } from "react/redux";
				import { hasActiveFilters } from "reducers/common/ColumnFilters";
				...
				function LibrariesFilterTagList(){
					let libraries = useSelector(state => state.libraries);
					let hasFilters = hasActiveFilters(libraries);
				}
			```
	*/
	let filters = state[COLUMN_FILTERS_NAMESPACE];
	if(!filters){
		return false;
	}

	let { active } = filters;
	if(!active){
		return false;
	}

	let keys = Object.keys(active);

	// We use Array.some() to terminate checking as soon as we find an active filter to save iterations.
	return keys.some((key) => {
		let filter = active[key];
		return isActiveFilter(filter);
	});
}

export function activeFilters(state){
	/*
		Sometimes you need to know what filters, or how many filters are active, rather than just knowing
		that active filters exist.

		This works like `hasActiveFilters`, but returns an array of active filters.
	*/

	let filters = state[COLUMN_FILTERS_NAMESPACE];
	if(!filters){
		return [];
	}

	let { active } = filters;
	if(!active){
		return [];
	}

	let keys = Object.keys(active);

	return keys.reduce((prev, key) => {
		let filter = active[key];
		if(isActiveFilter(filter)){
			return [...prev, filter];
		}
		return prev;
	}, []);
}

export function translateOptions(filter, dictionary = {}){
	/*
		Takes a whole columnFilter object (as you'd pass to `setColumnFilter`) or
		a filterPartial (as you'd pass to `updateColumnFilter`), as `filter`,
		and a dictionary, and replaces `options` with the dictionary translated version of it.

		(We can't just extract the dictionary from the filter, because it may not be in the partial).

		We take partials instead of just always translating the whole filter just in case we
		have some weird cases where there would be a circular translation, causing unwanted side effects.
	*/
	let { options } = filter;
	let { apiToUI } = dictionary;

	if(!apiToUI || !options){
		return filter;
	}

	let translatedOptions = options.map(item => apiToUI[item] || item);
	return {
		...filter,
		options: translatedOptions,
	};
}
